/**
 * useLiveTrack — manages a single live-tracking session for an alert.
 *
 * Responsibilities (creator side):
 *   • Calls POST /api/alerts/{alert_id}/track/start, persists the session_id
 *   • Subscribes to navigator.geolocation.watchPosition (high accuracy)
 *   • Throttled POST of /api/track/{session_id}/point — every 15 s OR
 *     every 25 m moved. Tracking is more granular than Driving Mode because
 *     the watcher relies on it for situational awareness.
 *   • Best-effort Wake Lock so the device doesn't sleep mid-transit
 *   • POST /api/track/{session_id}/stop on toggle off / unmount
 *
 * State exposed:
 *   { active, sessionId, pointCount, start, stop }
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import logging from "../utils/logging";
import { distanceMeters } from "../utils/geo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POINT_INTERVAL_MS = 15000;
const POINT_DISTANCE_M = 25;

export function useLiveTrack(alertId) {
  const [active, setActive] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [pointCount, setPointCount] = useState(0);

  const watchIdRef = useRef(null);
  const wakeLockRef = useRef(null);
  const lastPostedPosRef = useRef(null);
  const lastPostedAtRef = useRef(0);
  const sessionIdRef = useRef(null);

  const stop = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (watchIdRef.current != null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
    setActive(false);
    setSessionId(null);
    setPointCount(0);
    sessionIdRef.current = null;
    lastPostedPosRef.current = null;
    lastPostedAtRef.current = 0;
    if (sid) {
      try {
        await axios.post(`${API}/track/${sid}/stop`, {}, { withCredentials: true });
      } catch (err) {
        // Best-effort — server-side WS broadcast still fires once stopped
        logging.warn("Failed to stop tracking session", err);
      }
    }
  }, []);

  const start = useCallback(async () => {
    if (!alertId) return;
    if (!("geolocation" in navigator)) {
      toast.error("Geolocation is not supported on this device");
      return;
    }
    try {
      const r = await axios.post(`${API}/alerts/${alertId}/track/start`, {}, { withCredentials: true });
      const sid = r.data?.session?.session_id;
      if (!sid) throw new Error("No session id returned");
      sessionIdRef.current = sid;
      setSessionId(sid);
      setActive(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start tracking");
      return;
    }

    // Best-effort wake lock
    try {
      if ("wakeLock" in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      }
    } catch (err) {
      logging.warn("Wake lock denied", err);
    }

    const handlePosition = async (pos) => {
      const sid = sessionIdRef.current;
      if (!sid) return;
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      const tNow = Date.now();
      const here = { latitude: lat, longitude: lng };
      const moved = lastPostedPosRef.current
        ? distanceMeters(lastPostedPosRef.current, here)
        : Infinity;
      const elapsed = tNow - lastPostedAtRef.current;
      // Always post the very first sample so the breadcrumb has an origin
      if (lastPostedAtRef.current && moved < POINT_DISTANCE_M && elapsed < POINT_INTERVAL_MS) {
        return;
      }
      lastPostedPosRef.current = here;
      lastPostedAtRef.current = tNow;
      try {
        await axios.post(
          `${API}/track/${sid}/point`,
          {
            latitude: lat,
            longitude: lng,
            accuracy: pos.coords.accuracy ?? null,
            speed: typeof pos.coords.speed === "number" ? pos.coords.speed : null,
          },
          { withCredentials: true },
        );
        setPointCount((p) => p + 1);
      } catch (err) {
        // If the session was stopped server-side, surface once and stop watching
        if (err.response?.status === 400) {
          toast.message("Tracking session was stopped");
          stop();
        } else {
          logging.warn("Track point post failed", err);
        }
      }
    };

    const handleError = (err) => logging.warn("watchPosition error", err);

    watchIdRef.current = navigator.geolocation.watchPosition(
      handlePosition,
      handleError,
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 },
    );

    toast.success("Live tracking started — admins will see you move on the map");
  }, [alertId, stop]);

  // Auto-stop on unmount so a creator who navigates away doesn't keep
  // bleeding location to the network.
  useEffect(() => () => { stop(); }, [stop]);

  return { active, sessionId, pointCount, start, stop };
}
